import React from 'react';
import { Box, Paper, Skeleton, Stack, alpha } from '@mui/material';

function Shimmer(props: React.ComponentProps<typeof Skeleton>) {
  return (
    <Skeleton
      animation="wave"
      {...props}
      sx={(t) => ({
        bgcolor: t.tokens?.surface?.sunken ?? 'action.hover',
        ...((typeof props.sx === 'function' ? props.sx(t) : props.sx) as object),
      })}
    />
  );
}

export function ExecutiveSummarySkeleton() {
  return (
    <Box
      sx={(t) => ({
        p: 2,
        mb: 2,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        border: `1px solid ${t.palette.info.main}33`,
        bgcolor: `${t.palette.info.main}08`,
      })}
    >
      <Shimmer variant="rounded" width={140} height={16} sx={{ mb: 1.25, borderRadius: 0.75 }} />
      <Shimmer variant="rounded" width="92%" height={14} sx={{ mb: 0.75, borderRadius: 0.75 }} />
      <Shimmer variant="rounded" width="78%" height={14} sx={{ borderRadius: 0.75 }} />
    </Box>
  );
}

export function ReportingWindowSkeleton() {
  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={1.5}
      alignItems={{ xs: 'stretch', md: 'center' }}
      mt={1.5}
    >
      <Shimmer variant="rounded" height={72} sx={{ flex: 1, borderRadius: 1.5 }} />
      <Shimmer variant="rounded" width={48} height={20} sx={{ alignSelf: 'center', borderRadius: 0.75 }} />
      <Shimmer variant="rounded" height={72} sx={{ flex: 1, borderRadius: 1.5 }} />
    </Stack>
  );
}

const PHASE_ROW_COUNTS = [2, 2, 1, 2, 2];

export function ChainSkeleton() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(5, minmax(200px, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
        gap: 1,
        alignItems: 'start',
        overflowX: { xs: 'auto', lg: 'visible' },
        pb: { xs: 0.5, lg: 0 },
      }}
    >
      {PHASE_ROW_COUNTS.map((rows, i) => (
        <Box
          key={i}
          sx={(t) => ({
            borderRadius: `${t.ds?.radius?.md ?? 8}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            overflow: 'hidden',
          })}
        >
          <Box sx={{ px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
            <Shimmer variant="rounded" width={72} height={12} sx={{ borderRadius: 0.5 }} />
          </Box>
          <Stack spacing={0.75} sx={{ p: 1 }}>
            {Array.from({ length: rows }, (_, r) => (
              <Shimmer key={r} variant="rounded" height={rows === 1 ? 44 : 64} sx={{ width: '100%', borderRadius: 0.75 }} />
            ))}
          </Stack>
        </Box>
      ))}
    </Box>
  );
}

export function StageDetailNarrativeSkeleton() {
  return (
    <Paper
      variant="outlined"
      sx={(t) => ({
        mb: 3,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        borderColor: alpha(t.palette.info.main, 0.15),
        bgcolor: alpha(t.palette.info.main, 0.02),
        overflow: 'hidden',
      })}
    >
      <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Shimmer variant="rounded" width={140} height={18} sx={{ borderRadius: 0.75 }} />
      </Box>
      <Box sx={{ p: 2 }}>
        <Shimmer variant="rounded" width={100} height={14} sx={{ mb: 1, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="96%" height={14} sx={{ mb: 0.75, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="88%" height={14} sx={{ mb: 2, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width={60} height={14} sx={{ mb: 1, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="82%" height={14} sx={{ mb: 2, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width={120} height={14} sx={{ mb: 1, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="78%" height={14} sx={{ borderRadius: 0.75 }} />
      </Box>
    </Paper>
  );
}

export function BreakdownTableBodyShimmer({ rowCount = 8 }: { rowCount?: number }) {
  return (
    <>
      <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
        <Shimmer variant="rounded" width={280} height={32} sx={{ borderRadius: 1, maxWidth: '100%' }} />
      </Box>
      <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={2}>
          {['Dimension', 'This wk', 'Prior wk', 'Change', 'Status'].map((label) => (
            <Shimmer key={label} variant="rounded" width={label === 'Dimension' ? 90 : 56} height={12} sx={{ borderRadius: 0.5 }} />
          ))}
        </Stack>
      </Box>
      <Stack spacing={1.25} sx={{ p: 2, minHeight: 280 }}>
        {Array.from({ length: rowCount }).map((_, idx) => (
          <Shimmer key={idx} variant="rounded" width="100%" height={14} sx={{ borderRadius: 0.5 }} />
        ))}
      </Stack>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2,
        }}
      >
        <Shimmer variant="rounded" width={108} height={12} sx={{ borderRadius: 0.5 }} />
        <Shimmer variant="rounded" width={72} height={12} sx={{ borderRadius: 0.5 }} />
      </Box>
    </>
  );
}

export function BreakdownTableSkeleton({ rowCount = 6 }: { rowCount?: number }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Shimmer variant="rounded" width={160} height={18} sx={{ mb: 1.5, borderRadius: 0.75 }} />
      <Paper variant="outlined" sx={(t) => ({ borderRadius: `${t.ds?.radius?.sm ?? 6}px`, overflow: 'hidden' })}>
        <BreakdownTableBodyShimmer rowCount={rowCount} />
      </Paper>
    </Box>
  );
}

export function StageDetailSkeleton() {
  return (
    <Box
      sx={(t) => ({
        mt: 2.5,
        borderRadius: `${t.ds?.radius?.md ?? 8}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        overflow: 'hidden',
      })}
    >
      <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Shimmer variant="rounded" width={10} height={36} sx={{ borderRadius: 1 }} />
          <Box sx={{ flex: 1 }}>
            <Shimmer variant="rounded" width="38%" height={22} sx={{ mb: 0.75, borderRadius: 0.75 }} />
            <Shimmer variant="rounded" width="52%" height={14} sx={{ borderRadius: 0.75 }} />
          </Box>
        </Stack>
      </Box>
      <Box sx={{ p: 2.5 }}>
        <Shimmer variant="rounded" width={120} height={16} sx={{ mb: 1.5, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="96%" height={14} sx={{ mb: 0.75, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="88%" height={14} sx={{ mb: 2, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width={80} height={16} sx={{ mb: 1.5, borderRadius: 0.75 }} />
        <Shimmer variant="rounded" width="72%" height={14} sx={{ borderRadius: 0.75 }} />
      </Box>
    </Box>
  );
}
